import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  SxProps,
  Tab,
  Tabs,
  TabsProps,
  Theme,
} from "@mui/material";
import type { TabProps } from "@mui/material";

type TabbedPageCardVariant = "card" | "flat";

export interface TabbedPageCardTab {
  id?: string;
  label: React.ReactNode;
  content: React.ReactNode;
  disabled?: boolean;
  icon?: TabProps["icon"];
  iconPosition?: TabProps["iconPosition"];
}

export interface TabbedPageCardProps {
  tabs: TabbedPageCardTab[];
  ariaLabel?: string;
  idPrefix?: string;
  variant?: TabbedPageCardVariant;
  fullHeight?: boolean;
  selectedTab?: number;
  onTabChange?: (newValue: number) => void;
  tabsProps?: Partial<TabsProps>;
  sx?: SxProps<Theme>;
  panelSx?: SxProps<Theme>;
}

const toSxArray = (sx?: SxProps<Theme>) =>
  Array.isArray(sx) ? sx : sx ? [sx] : [];

const getTabId = (idPrefix: string, index: number) => `${idPrefix}-tab-${index}`;
const getTabPanelId = (idPrefix: string, index: number) =>
  `${idPrefix}-tabpanel-${index}`;

const TabbedPageCard: React.FC<TabbedPageCardProps> = ({
  tabs,
  ariaLabel,
  idPrefix = "page",
  variant = "card",
  fullHeight = false,
  selectedTab: controlledSelectedTab,
  onTabChange,
  tabsProps,
  sx,
  panelSx,
}) => {
  const [uncontrolledSelectedTab, setUncontrolledSelectedTab] = useState(0);
  const selectedTab = controlledSelectedTab ?? uncontrolledSelectedTab;
  const isCard = variant === "card";
  const activeTab = tabs[selectedTab];
  const hasIconTabs = tabs.some((tab) => Boolean(tab.icon));

  useEffect(() => {
    if (selectedTab >= tabs.length) {
      if (controlledSelectedTab === undefined) {
        setUncontrolledSelectedTab(0);
      } else {
        onTabChange?.(0);
      }
    }
  }, [controlledSelectedTab, onTabChange, selectedTab, tabs.length]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    if (controlledSelectedTab === undefined) {
      setUncontrolledSelectedTab(newValue);
    }
    onTabChange?.(newValue);
  };

  const content = (
    <>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          aria-label={ariaLabel}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          {...tabsProps}
          sx={[
            {
              minHeight: hasIconTabs ? 44 : undefined,
              "& .MuiTabs-scrollButtons.Mui-disabled": {
                opacity: 0.3,
              },
            },
            ...toSxArray(tabsProps?.sx),
          ]}
        >
          {tabs.map((tab, index) => (
            <Tab
              key={tab.id ?? index}
              label={tab.label}
              icon={tab.icon}
              iconPosition={tab.iconPosition}
              disabled={tab.disabled}
              id={getTabId(idPrefix, index)}
              aria-controls={getTabPanelId(idPrefix, index)}
              sx={
                tab.icon
                  ? {
                      minHeight: 44,
                      py: 0.75,
                      "& .MuiTab-iconWrapper": {
                        fontSize: 20,
                      },
                    }
                  : undefined
              }
            />
          ))}
        </Tabs>
      </Box>

      {activeTab && (
        <Box
          role="tabpanel"
          id={getTabPanelId(idPrefix, selectedTab)}
          aria-labelledby={getTabId(idPrefix, selectedTab)}
          sx={[
            {
              height: fullHeight ? "100%" : undefined,
              overflow: fullHeight ? "auto" : undefined,
            },
            ...toSxArray(panelSx),
          ]}
        >
          {activeTab.content}
        </Box>
      )}
    </>
  );

  if (!isCard) {
    return (
      <Box sx={[{ minWidth: 0 }, ...toSxArray(sx)]}>
        {content}
      </Box>
    );
  }

  return (
    <Paper
      elevation={1}
      sx={[
        {
          borderRadius: 2,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          flex: fullHeight ? 1 : undefined,
          minHeight: fullHeight ? 0 : undefined,
        },
        ...toSxArray(sx),
      ]}
    >
      {content}
    </Paper>
  );
};

export default TabbedPageCard;
